import * as R from 'ramda';
import * as P from 'plow-js';
import { createReducer } from 'redux-act';
// common
import { updateReportFromReportListSuccess } from '../../common/actions';
// helpers/constants
import * as G from '../../helpers';
import * as GC from '../../constants';
// feature fleet-documents
import * as A from './actions';
//////////////////////////////////////////////////

const defaultState = {
  summary: {},
  itemList: {},
  totalCount: 0,
  loading: false,
  usedReport: null,
  filterParams: {},
  pageVisited: false,
  titleSortValues: {},
  reportPending: false,
  tableTitleFilters: {},
  availableReports: null,
  pagination: { limit: 20, offset: 0 },
};

const initialState = {
  reportType: null,
};

const setInitialState = (state, reportType) => (
  P.$all(
    P.$set('reportType', reportType),
    P.$set(reportType, defaultState),
    state,
  )
);

const setInitialStateOmitReport = (state, omit) => {
  const { reportType } = state;

  const reportState = R.pathOr({}, [reportType], state);

  const {
    usedReport,
    pageVisited,
    filterParams,
    titleSortValues,
    tableTitleFilters,
  } = reportState;

  const toOmit = R.pick(R.or(omit, []), reportState);

  return P.$set(reportType, {
    ...defaultState,
    ...toOmit,
    usedReport,
    pageVisited,
    filterParams,
    titleSortValues,
    tableTitleFilters,
  }, state);
};

const setReportType = (state, reportType) => P.$set('reportType', reportType, state);

const setListLoading = (state, loading) => P.$set(`${state.reportType}.loading`, loading, state);

const setReportPending = (state) => P.$set(`${state.reportType}.reportPending`, true, state);

const setReports = (state, reports) => P.$set(`${state.reportType}.availableReports`, reports, state);

const resetListAndPagination = (state) => {
  const { reportType } = state;

  return P.$all(
    P.$set(`${reportType}.itemList`, {}),
    P.$set(`${reportType}.totalCount`, 0),
    P.$set(`${reportType}.pagination`, defaultState.pagination),
    state,
  );
};

const setUsedReport = (state, data) => {
  const { reportType } = state;

  return P.$all(
    P.$set(`${reportType}.itemList`, {}),
    P.$set(`${reportType}.totalCount`, 0),
    P.$set(`${reportType}.usedReport`, data),
    P.$set(`${reportType}.titleSortValues`, {}),
    P.$set(`${reportType}.reportPending`, false),
    P.$set(`${reportType}.tableTitleFilters`, {}),
    P.$set(`${reportType}.pagination`, defaultState.pagination),
    state,
  );
};

const getItemListSuccess = (state, { data, guids }) => {
  const { reportType } = state;

  const { itemList, pagination } = R.pathOr({}, [reportType], state);

  const { results, totalCount } = data;

  if (R.and(G.isNotNilAndNotEmpty(guids), R.equals(R.length(results), 1))) {
    const result = R.head(results);
    const guid = G.getGuidFromObject(result);

    if (G.isNotNil(R.prop(guid, itemList))) {
      return P.$set(`${reportType}.itemList.${guid}`, R.mergeRight(R.prop(guid, itemList), result), state);
    }

    const remappedList = R.compose(
      R.assoc(guid, R.compose(
        R.assoc('index', 0),
        R.assoc('selected', false),
      )(result)),
      R.map((item) => R.assoc('index', R.inc(R.prop('index', item)), item)),
    )(itemList);

    return P.$all(
      P.$set(`${reportType}.itemList`, remappedList),
      P.$set(`${reportType}.pagination.offset`, R.inc(pagination.offset)),
      P.$set(`${reportType}.totalCount`, R.inc(R.pathOr(0, [reportType, 'totalCount'], state))),
      state,
    );
  }

  const indexAdditional = G.ifElse(
    R.isNil(itemList),
    0,
    R.length(R.values(itemList)),
  );

  const newItems = results.map((item, index) => R.mergeRight(
    item,
    {
      selected: false,
      index: R.add(index, indexAdditional),
    },
  ));

  const list = R.mergeRight(itemList, R.indexBy(R.prop(GC.FIELD_GUID), newItems));
  const newOffset = R.add(pagination.offset, pagination.limit);

  return P.$all(
    P.$set(`${reportType}.itemList`, list),
    P.$set(`${reportType}.pageVisited`, true),
    P.$set(`${reportType}.totalCount`, totalCount),
    P.$set(
      `${reportType}.pagination.offset`,
      G.ifElse(
        R.gt(totalCount, newOffset),
        newOffset,
        totalCount,
      ),
    ),
    state,
  );
};

const setTableTitleFilter = (state, data) => {
  const { reportType } = state;

  const { tableTitleFilters } = R.pathOr({}, [reportType], state);

  const filterValues = G.getTableTitleFilterValues(tableTitleFilters, data);

  return P.$set(`${reportType}.tableTitleFilters`, filterValues, state);
};

const setTableTitleSort = (state, data) => {
  const { reportType } = state;

  const { titleSortValues } = R.pathOr({}, [reportType], state);

  const sortValues = G.getTableTitleSortValues(titleSortValues, data);

  return P.$set(`${reportType}.titleSortValues`, sortValues, state);
};

const getSummarySuccess = (state, summary) => P.$set(`${state.reportType}.summary`, summary, state);

const handleUpdateReportFromReportListSuccess = (state, data) => {
  if (G.isNilOrEmpty(data)) return state;

  const { reportType } = state;

  const updatedGuid = G.getGuidFromObject(data);
  const usedUpdated = R.pathEq(updatedGuid, [reportType, 'usedReport', GC.FIELD_GUID], state);

  if (G.isTrue(usedUpdated)) return P.$set(`${reportType}.usedReport`, data, state);

  return state;
};

export default createReducer({
  [A.setReports]: setReports,
  [A.setReportType]: setReportType,
  [A.setUsedReport]: setUsedReport,
  [A.setListLoading]: setListLoading,
  [A.setInitialState]: setInitialState,
  [A.setReportPending]: setReportPending,
  [A.getSummarySuccess]: getSummarySuccess,
  [A.setTableTitleSort]: setTableTitleSort,
  [A.getItemListSuccess]: getItemListSuccess,
  [A.setTableTitleFilter]: setTableTitleFilter,
  [A.resetListAndPagination]: resetListAndPagination,
  [A.setInitialStateOmitReport]: setInitialStateOmitReport,
  [updateReportFromReportListSuccess]: handleUpdateReportFromReportListSuccess,
}, initialState);
